// ProjectRelevanceBadge — honest, live project-domain-overlap indicator for
// an Ecosystem card. Renders nothing when there is no real signal to show
// (Ecosystem Marketplace project-domain-overlap matching redesign) — never a
// fabricated relevance number or match percentage.
import { Target } from 'lucide-react'

interface ProjectRelevanceBadgeProps {
  /** number of the caller's real active projects with a computed domain overlap */
  relevantCount: number
  /** the actual overlapping domain labels */
  sharedDomains: string[]
  /**
   * Live embedding-similarity percentage (0-100) — the real `cosine_sim`
   * returned by POST /ecosystem/search-by-project for this profile against
   * the selected project's aggregated document embedding. Only present when
   * a project filter is active; omitted (not fabricated) otherwise.
   */
  matchPercent?: number
}

export default function ProjectRelevanceBadge({ relevantCount, sharedDomains, matchPercent }: ProjectRelevanceBadgeProps) {
  const hasRelevance = relevantCount > 0
  const hasMatchPercent = typeof matchPercent === 'number' && Number.isFinite(matchPercent) && matchPercent > 0

  // Honest empty state: if we can't compute a real overlap or match score,
  // render nothing rather than a fake "0 matches" line.
  if (!hasRelevance && !hasMatchPercent) return null

  return (
    <div
      data-testid="project-relevance-badge"
      className="flex items-start gap-1.5 rounded-lg bg-iflow-50 border border-iflow-100 px-2.5 py-1.5 mb-3 text-xs text-iflow-900"
    >
      <Target className="w-3.5 h-3.5 text-iflow-600 mt-0.5 shrink-0" aria-hidden />
      <div className="min-w-0 leading-relaxed">
        {hasRelevance && (
          <p>
            Relevant to <strong>{relevantCount}</strong> of your project{relevantCount === 1 ? '' : 's'}
            {sharedDomains.length > 0 && (
              <>
                {' '}
                · shares <strong>{sharedDomains.join(', ')}</strong>
              </>
            )}
          </p>
        )}
        {hasMatchPercent && (
          <p className={hasRelevance ? 'mt-0.5' : ''}>
            <strong>{Math.round(matchPercent as number)}%</strong> embedding match to this project
          </p>
        )}
      </div>
    </div>
  )
}
